import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Divider,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format, differenceInCalendarDays } from 'date-fns';
import PageHeader from '../components/PageHeader';
import { getActiveProjectId, subscribeProjectChange } from '../services/projectContext';

// Use relative URL for proxy support in dev, or environment variable in production
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Status configurations
const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; progress: number }> = {
  'draft': { label: 'Draft', color: 'default', progress: 0 },
  'submitted': { label: 'Submitted', color: 'info', progress: 20 },
  'in_review': { label: 'In Review', color: 'primary', progress: 40 },
  'additional_info': { label: 'Additional Info', color: 'warning', progress: 50 },
  'approved': { label: 'Approved', color: 'success', progress: 100 },
  'rejected': { label: 'Rejected', color: 'error', progress: 100 },
  'withdrawn': { label: 'Withdrawn', color: 'default', progress: 100 },
};

interface Consent {
  id: number;
  consent_reference: string;
  consent_type: string;
  category: string;
  project_name: string;
  status: string;
  created_at: string;
  submission_date: string | null;
  target_completion_date: string | null;
  criticality: number;
}

interface ConsentType {
  id: number;
  name: string;
  code: string;
}

interface Category {
  id: number;
  name: string;
  code: string;
}

const Consents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState(searchParams.get('criticality') || '');
  const [deliveryUrgencyFilter, setDeliveryUrgencyFilter] = useState(searchParams.get('delivery_urgency') || '');
  const overdueFilter = searchParams.get('attention') === 'overdue';
  
  // Reference data
  const [categories, setCategories] = useState<Category[]>([]);
  const [consentTypes, setConsentTypes] = useState<ConsentType[]>([]);
  const [projectId, setProjectId] = useState<number | null>(getActiveProjectId());

  useEffect(() => subscribeProjectChange(project => setProjectId(project.id)), []);

  // Fetch consents
  const fetchConsents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (criticalityFilter) params.append('criticality', criticalityFilter);
      if (projectId) params.append('project_id', String(projectId));
      
      const response = await fetch(`${API_BASE}/consents/?${params}`);
      if (!response.ok) throw new Error('Failed to fetch consents');
      const data = await response.json();
      // Handle the API response format which has consents array
      const consentsList = data.consents || data;
      // Map API response to expected format
      const mapped = consentsList.map((c: any) => ({
        id: c.id,
        consent_reference: c.reference,
        consent_type: c.consent_type?.name || '',
        category: c.consent_type?.category || '',
        project_name: c.project?.name || '',
        status: c.status,
        created_at: c.created_at,
        submission_date: c.submission_date,
        target_completion_date: c.target_approval_date,
        criticality: c.criticality || 3,
      }));
      setConsents(mapped);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading consents');
    } finally {
      setLoading(false);
    }
  };

  // Fetch reference data
  const fetchReferenceData = async () => {
    try {
      const [catRes, typeRes] = await Promise.all([
        fetch(`${API_BASE}/consents/categories`),
        fetch(`${API_BASE}/consents/consent-types`),
      ]);
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }
      if (typeRes.ok) {
        const typeData = await typeRes.json();
        setConsentTypes(typeData);
      }
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  useEffect(() => {
    fetchConsents();
    fetchReferenceData();
  }, [statusFilter, categoryFilter, criticalityFilter, projectId]);

  // Keep search in sync if the user arrives via the global top-bar search
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchQuery(q);
    setStatusFilter(searchParams.get('status') || '');
    setCategoryFilter(searchParams.get('category') || '');
    setCriticalityFilter(searchParams.get('criticality') || '');
    setDeliveryUrgencyFilter(searchParams.get('delivery_urgency') || '');
  }, [searchParams]);

  const updateFilterUrl = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const setStatus = (value: string) => { setStatusFilter(value); updateFilterUrl('status', value); };
  const criticalityMeta = (value: number) => ({
    label: ['Administrative', 'Low', 'Medium', 'High', 'Critical'][Math.max(1, Math.min(5, value)) - 1],
    color: value >= 5 ? 'error' : value >= 4 ? 'warning' : value === 3 ? 'info' : value === 2 ? 'success' : 'default',
  } as const);

  // Filter consents by search
  const filteredConsents = consents.filter(consent => {
    if (overdueFilter) {
      const days = daysToTarget(consent.target_completion_date);
      if (days === null || days >= 0 || ['approved', 'rejected', 'withdrawn'].includes(consent.status)) return false;
    }
    if (criticalityFilter && consent.criticality !== Number(criticalityFilter)) return false;
    if (deliveryUrgencyFilter) {
      const days = daysToTarget(consent.target_completion_date);
      const matchesUrgency = deliveryUrgencyFilter === 'high' ? days !== null && days <= 14 : deliveryUrgencyFilter === 'medium' ? days !== null && days > 14 && days <= 30 : days === null || days > 30;
      if (!matchesUrgency) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      consent.consent_reference?.toLowerCase().includes(query) ||
      consent.consent_type?.toLowerCase().includes(query) ||
      consent.project_name?.toLowerCase().includes(query)
    );
  });

  // Paginate
  const paginatedConsents = filteredConsents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  };

  const getStatusChip = (status: string) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'default' as const, progress: 0 };
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small" 
        sx={{ fontWeight: 500 }}
      />
    );
  };

  // Days remaining until target approval date (negative = overdue)
  function daysToTarget(dateStr: string | null) {
    if (!dateStr) return null;
    return differenceInCalendarDays(new Date(dateStr), new Date());
  };

  // Status counts for the summary strip
  const statusCounts = consents.reduce((acc, consent) => {
    const status = consent.status || 'draft';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const summaryStats = [
    { label: 'Total', count: consents.length },
    { label: 'Draft', count: statusCounts['draft'] || 0 },
    { label: 'Submitted', count: statusCounts['submitted'] || 0 },
    { label: 'In Review', count: statusCounts['in_review'] || 0 },
    { label: 'Approved', count: statusCounts['approved'] || 0 },
    { label: 'Rejected', count: statusCounts['rejected'] || 0 },
  ];

  return (
    <Box>
      {/* Header */}
      <PageHeader title="Consents" subtitle={overdueFilter ? `Showing ${filteredConsents.length} overdue consents requiring action.` : 'Track consent obligations, status and delivery dates.'} action={<><Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchConsents}>Refresh</Button><Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/consents/new')}>New Consent</Button></>} />

      {/* Summary strip */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={{ xs: 1, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />} flexWrap="wrap" sx={{ width: '100%', justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
          {summaryStats.map((stat) => {
            const filterKey = stat.label === 'Total' ? '' : stat.label.toLowerCase().replace(' ', '_');
            return (
              <Box
                key={stat.label}
                onClick={() => setStatus(filterKey)}
                sx={{ cursor: 'pointer', minWidth: 0, flex: { xs: '1 1 30%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' }, opacity: statusFilter && statusFilter !== filterKey ? 0.5 : 1 }}
              >
                <Typography variant="h5" fontWeight={700}>{stat.count}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      <Box data-tour-id="consent-register" data-tour-ready={!loading && !error ? 'true' : undefined}>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search consents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Criticality</InputLabel>
              <Select value={criticalityFilter} label="Criticality" onChange={(e) => { setCriticalityFilter(e.target.value); updateFilterUrl('criticality', e.target.value); }}>
                <MenuItem value="">All priorities</MenuItem>
                {[5, 4, 3, 2, 1].map(level => <MenuItem key={level} value={level}>{level} · {criticalityMeta(level).label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => { setCategoryFilter(e.target.value); updateFilterUrl('category', e.target.value); }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" textAlign="right">
              {filteredConsents.length} consent{filteredConsents.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
        {(overdueFilter || deliveryUrgencyFilter) && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {overdueFilter && <Chip label="Overdue" color="error" onDelete={() => { const next = new URLSearchParams(searchParams); next.delete('attention'); setSearchParams(next); }} />}
          {deliveryUrgencyFilter && <Chip label={`${deliveryUrgencyFilter[0].toUpperCase() + deliveryUrgencyFilter.slice(1)} deadline pressure`} color="warning" onDelete={() => { const next = new URLSearchParams(searchParams); next.delete('delivery_urgency'); setSearchParams(next); }} />}
          <Typography variant="caption" color="text.secondary">Filter loaded from Project Overview</Typography>
        </Stack>}
      </Paper>

      {/* Consents Table */}
      <Paper>
        {loading && <LinearProgress />}
        
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button onClick={fetchConsents} sx={{ mt: 2 }}>Retry</Button>
          </Box>
        )}

        {!loading && !error && (
          <>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto', '& table': { minWidth: 820 } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Criticality</TableCell>
                    <TableCell>Target Date</TableCell>
                    <TableCell align="right">Days to Target</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConsents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">
                          No consents found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedConsents.map((consent) => (
                      <TableRow 
                        key={consent.id}
                        hover
                        sx={{ cursor: 'pointer', ...(overdueFilter ? { borderLeft: '3px solid', borderColor: 'error.main', bgcolor: '#FFF8F8' } : {}) }}
                        onClick={() => navigate(`/consents/${consent.id}`)}
                      >
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            color="primary"
                            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem' }}
                          >
                            {consent.consent_reference}
                          </Typography>
                        </TableCell>
                        <TableCell>{consent.consent_type}</TableCell>
                        <TableCell>{consent.category}</TableCell>
                        <TableCell>{consent.project_name || '-'}</TableCell>
                        <TableCell>{getStatusChip(consent.status)}</TableCell>
                        <TableCell><Chip size="small" label={`${consent.criticality} · ${criticalityMeta(consent.criticality).label}`} color={criticalityMeta(consent.criticality).color} /></TableCell>
                        <TableCell>{formatDate(consent.target_completion_date)}</TableCell>
                        <TableCell align="right">
                          {(() => {
                            const days = daysToTarget(consent.target_completion_date);
                            if (days === null || ['approved', 'rejected', 'withdrawn'].includes(consent.status)) return '—';
                            return (
                              <Chip
                                size="small"
                                label={days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                                sx={{
                                  bgcolor: days < 0 ? '#DC2626' : days <= 7 ? '#FEF3C7' : '#F1F3F6',
                                  color: days < 0 ? '#fff' : days <= 7 ? '#92400E' : 'text.primary',
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/consents/${consent.id}`);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/consents/${consent.id}/edit`);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack spacing={1.25} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.5 }}>
              {paginatedConsents.length === 0 ? <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No consents found</Typography> : paginatedConsents.map((consent) => {
                const days = daysToTarget(consent.target_completion_date);
                return <Paper key={consent.id} onClick={() => navigate(`/consents/${consent.id}`)} sx={{ p: 1.75, cursor: 'pointer', borderLeft: overdueFilter ? '3px solid' : 'none', borderColor: overdueFilter ? 'error.main' : undefined }}>
                  <Typography fontWeight={700} color="primary" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem' }}>{consent.consent_reference}</Typography>
                  <Typography fontWeight={600} sx={{ mt: 0.5 }}>{consent.consent_type}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{consent.category} · {consent.project_name || 'No project'}</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>{getStatusChip(consent.status)}<Chip size="small" label={`${consent.criticality} · ${criticalityMeta(consent.criticality).label}`} color={criticalityMeta(consent.criticality).color} />{days !== null && !['approved', 'rejected', 'withdrawn'].includes(consent.status) && <Chip size="small" label={days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`} color={days < 0 ? 'error' : days <= 7 ? 'warning' : 'default'} />}</Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}><Typography variant="caption" color="text.secondary">Target: {formatDate(consent.target_completion_date)}</Typography><Stack direction="row"><IconButton size="small" aria-label="View consent" onClick={(e) => { e.stopPropagation(); navigate(`/consents/${consent.id}`); }}><ViewIcon fontSize="small" /></IconButton><IconButton size="small" aria-label="Edit consent" onClick={(e) => { e.stopPropagation(); navigate(`/consents/${consent.id}/edit`); }}><EditIcon fontSize="small" /></IconButton></Stack></Stack>
                </Paper>;
              })}
            </Stack>
            
            <TablePagination
              component="div"
              count={filteredConsents.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
      </Box>
    </Box>
  );
};

export default Consents;
